import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";
import { format } from "date-fns";
import { CalendarIcon, Link2 } from "lucide-react";

import { DefineJointDialog } from "../../../components/portfolio-risk-concentration/DefineJointDialog";
import { PageHeader } from "../../../components/portfolio-risk-concentration/PageHeader";
import { thr } from "../../../components/portfolio-risk-concentration/format";
import {
  INVESTOR_LABEL,
  PrcFilterBar,
  matchesFacility,
  matchesProduct,
  usePrcFilters,
} from "../../../components/portfolio-risk-concentration/PrcFilterBar";
import { useInvestorScope } from "../../../utils/portfolio-risk-concentration/api/facilities";
import { ManageLimitsShell } from "../../../components/portfolio-risk-concentration/StepShell";
import { Badge } from "../../../ui/badge";
import { Button } from "../../../ui/button";
import { Calendar } from "../../../ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Checkbox } from "../../../ui/checkbox";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";
import { PrcDialogContent } from "../../../components/portfolio-risk-concentration/PrcDialogContent";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../../../ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../ui/tabs";
import { Textarea } from "../../../ui/textarea";
import {
  useJointDefs,
  type JointDef,
} from "../../../utils/portfolio-risk-concentration/api/joints";
import {
  isJointChange,
  useLimitConfigStore,
  type FacilityType,
  type LimitConfigRow,
  type LimitProposal,
  type ProposeInput,
} from "../../../utils/portfolio-risk-concentration/api/limit-config";
import {
  defaultOperator,
  valuesHint,
  type PrcDimension,
  type PrcMatchOperator,
} from "../../../utils/portfolio-risk-concentration/api/prc-limits";
import {
  NAVITAS_DIMENSIONS,
  defaultNavitasOperator,
  dimSpec,
  navitasArity,
  navitasValuesHint,
  operatorLabel,
  type NavitasDimension,
  type NavitasMeasure,
  type NavitasOperator,
} from "../../../utils/portfolio-risk-concentration/api/navitas-limits";
import { cn } from "../../../utils/common/cn";
import { hasTier } from "../../../components/portfolio-risk-concentration/access-control";

const DIMENSIONS: { value: PrcDimension; label: string }[] = [
  { value: "state", label: "State" },
  { value: "asset_industry", label: "Asset segment" },
  { value: "sic_code", label: "Industry (SIC)" },
  { value: "obligor", label: "Obligor (top-N)" },
];

const usd = (v: number) =>
  v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const capText = (v: number | null) => (v === null ? "—" : usd(v));

/**
 * One line describing a copied limit's rule, for the activation list.
 *
 * Read-only and deliberately terse — enough to tell "Top 3 equipment types" from "Top 10"
 * without opening anything. The rule came from navitas_limit_config and is not editable
 * here: activating a category decides its THRESHOLD, not what it measures.
 */
const RULE_DIM_LABEL: Record<string, string> = {
  state: "State",
  asset_industry: "Asset segment",
  sic_code: "SIC code",
  industry: "Industry (SIC, ex-franchise)",
  obligor: "Obligor",
  equipment: "Equipment type",
  credit_grade: "Credit grade",
  guarantor: "Personal guaranty",
  payment_type: "Payment type",
  office: "Office",
  fico: "FICO",
  original_term: "Original term",
  remaining_term: "Remaining term",
  residual: "Residual",
};

function ruleText(r: LimitConfigRow): string {
  const dim = RULE_DIM_LABEL[r.dimension ?? ""] ?? r.dimension ?? "";
  const v = r.matchValues ?? [];
  switch (r.matchOperator) {
    case "TOP_N":
      return `Top ${v[0]} by ${dim.toLowerCase()}`;
    case "RANK_AT":
      return `Ranked #${v[0]} by ${dim.toLowerCase()}`;
    case "MEASURE_SUM":
      return `Total ${r.measure === "pv_unguaranteed_residual" ? "unguaranteed residual" : "net investment"}`;
    case "SIC_RANGE":
      return `${dim} ${v[0]}–${v[1]}`;
    case "BETWEEN":
      return `${dim} between ${v[0]} and ${v[1]}`;
    case "IN":
      return `${dim} in ${v.join(", ")}`;
    case "NOT_IN":
      return `${dim} not in ${v.join(", ")}`;
    case "GT":
      return `${dim} > ${v[0]}`;
    case "GTE":
      return `${dim} ≥ ${v[0]}`;
    case "LT":
      return `${dim} < ${v[0]}`;
    case "LTE":
      return `${dim} ≤ ${v[0]}`;
    case "EQ":
      return `${dim} = ${v[0]}`;
    case "NE":
      return `${dim} ≠ ${v[0]}`;
    case "LIKE":
      return `${dim} matches ${v[0]}`;
    case "NOT_LIKE":
      return `${dim} does not match ${v[0]}`;
    default:
      return `${dim} ${r.matchOperator ?? ""} ${v.join(", ")}`.trim();
  }
}

/** One row of Proposed View: an approved limit or joint, with its pending proposal
 *  (if any) laid over it, or an ADD proposal that has nothing approved behind it. */
interface ComparisonRow {
  key: string;
  kind: "limit" | "joint";
  categoryGroup: string;
  limitDesc: string;
  /** For a joint this is its alert distance, not a threshold — see the note where
   *  these rows are built. Null when the row IS the proposal (an ADD). */
  currentThresholdPct: number | null;
  currentCapDollars: number | null;
  effectiveStartDate: string | null;
  proposal: LimitProposal | null;
  isNew: boolean;
  memberCount?: number;
}

const CHANGE_TYPE_LABEL: Record<string, string> = {
  ADD: "Limit",
  MODIFY: "Limit",
  ADD_JOINT: "Add joint",
  MODIFY_JOINT: "Edit joint",
  DELETE_JOINT: "Archive joint",
};

interface FormState {
  limitId: string | null;
  categoryGroup: string;
  limitDesc: string;
  thresholdPct: string;
  capitalCap: string;
  effectiveStartDate: Date;
  reason: string;
  // internal-ADD taxonomy (prc_limit_config)
  dimension: PrcDimension;
  matchOperator: PrcMatchOperator;
  matchValues: string;
  // external-ADD taxonomy (navitas_limit_config)
  /** The facility a new external limit is for: one of the investor's facilities with risk limits ("" until one is chosen). */
  facilityCode: string;
  extDimension: NavitasDimension;
  extOperator: NavitasOperator;
  measure: NavitasMeasure;
  // external-MODIFY: which snapshots the threshold change applies to
  applyFileDates: string[];
}

function emptyForm(): FormState {
  return {
    limitId: null,
    categoryGroup: "",
    limitDesc: "",
    thresholdPct: "",
    capitalCap: "",
    effectiveStartDate: new Date(),
    reason: "",
    dimension: "state",
    matchOperator: "IN",
    matchValues: "",
    facilityCode: "",
    extDimension: "obligor",
    extOperator: "TOP_N",
    measure: "net_investment",
    applyFileDates: [],
  };
}

function formFromRow(row: LimitConfigRow): FormState {
  return {
    ...emptyForm(),
    limitId: row.limitId,
    categoryGroup: row.categoryGroup,
    limitDesc: row.limitDesc,
    thresholdPct: String(row.thresholdPct),
    capitalCap: row.capitalCapDollars === null ? "" : String(row.capitalCapDollars),
    effectiveStartDate: new Date(`${row.effectiveStartDate}T00:00:00`),
    // the limit's own facility: what an external MODIFY proposal is filed under
    facilityCode: row.facilityCode,
  };
}

/**
 * The proposal summary inside the approval queue.
 *
 * Extracted and branched because a joint proposal has no threshold change and no capital
 * cap: rendering the limit layout for one produced "Threshold — → 0%" and an empty cap
 * line. Everything else in the queue — notes, required-note validation, Approve/Reject,
 * busy state — is already generic and untouched, which is the payoff of routing joints
 * through the existing proposal shape rather than a parallel flow.
 */
function ProposalSummary({ p, isActivation }: { p: LimitProposal; isActivation?: boolean }) {
  const joint = isJointChange(p.changeType);
  const members = p.memberLimitDescs ?? p.jointMemberRefs ?? [];

  if (!joint) {
    return (
      <div>
        <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
          {p.facilityCode} · {p.categoryGroup}
        </div>
        <div className="font-medium">
          {p.limitDesc}
          {/* An activation travels as a MODIFY, so without this it reads as a repricing
              of a limit already in force. It is the opposite: the limit is inert today
              and approving starts enforcing it. */}
          {isActivation && (
            <Badge
              variant="outline"
              className="ml-1.5 text-[10px] uppercase tracking-[0.1em] text-amber"
            >
              Activation
            </Badge>
          )}
        </div>
        {isActivation && (
          <p className="mt-1 text-xs text-muted-foreground">
            Not currently enforced. Approving switches this limit on; the threshold below is
            the external book&apos;s value it was seeded from.
          </p>
        )}
        <div className="mt-1 text-sm tabular-nums">
          Threshold {thr(p.oldThresholdPct)} →{" "}
          <span className="font-medium">{thr(p.newThresholdPct)}</span>
          {(p.oldCapitalCapDollars !== null || p.newCapitalCapDollars !== null) && (
            <>
              {" · Capital cap "}
              {capText(p.oldCapitalCapDollars)} →{" "}
              <span className="font-medium">{capText(p.newCapitalCapDollars)}</span>
            </>
          )}
        </div>
        <div className="mt-1 text-xs text-muted-foreground">
          Effective {p.effectiveStartDate} · proposed by {p.proposedByRole}
        </div>
        <p className="mt-2 max-w-xl text-sm text-muted-foreground">{p.reason}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
        {p.facilityCode} · {CHANGE_TYPE_LABEL[p.changeType ?? ""] ?? "Joint"}
      </div>
      <div className="flex items-start gap-1.5 font-medium">
        <Link2 className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
        {p.jointName ?? p.limitDesc}
      </div>
      {members.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {members.map((m) => (
            <Badge key={m} variant="secondary" className="text-[11px]">
              {m}
            </Badge>
          ))}
        </div>
      )}
      <div className="mt-1.5 text-sm tabular-nums">
        Alert threshold {thr(p.oldThresholdPct)} →{" "}
        <span className="font-medium">{thr(p.newThresholdPct)}</span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Headroom is computed daily as the smallest of the members' own headrooms — there is no
        threshold to set on the joint itself.
      </p>
      {p.changeType === "DELETE_JOINT" && (
        <p className="mt-1 text-xs text-destructive">
          Archives this joint — its alerts stop firing on approval.
        </p>
      )}
      <div className="mt-1 text-xs text-muted-foreground">
        Effective {p.effectiveStartDate} · proposed by {p.proposedByRole}
      </div>
      <p className="mt-2 max-w-xl text-sm text-muted-foreground">{p.reason}</p>
    </div>
  );
}

function StatusBadge({ status }: { status: LimitProposal["status"] }) {
  return (
    <Badge
      variant="outline"
      className={cn(
        "text-[10px] font-semibold uppercase tracking-[0.1em]",
        status === "APPROVED" &&
          "border-[color:var(--approve)]/40 bg-[color:var(--approve)]/10 text-[color:var(--approve)]",
        status === "REJECTED" && "border-destructive/50 bg-destructive/10 text-destructive",
        status === "PENDING" && "border-amber/50 bg-amber-dim/40 text-amber",
        status === "SUPERSEDED" && "text-muted-foreground",
      )}
    >
      {status}
    </Badge>
  );
}

export default function ManageLimitsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const facilityParam = searchParams.get("facility") as FacilityType | null;
  const limitIdParam = searchParams.get("limitId") ?? undefined;
  const fromParam = searchParams.get("from") ?? undefined;

  // Which view is active:
  //   "approved"  the live limits AND joints
  //   "proposed"  the same rows with in-flight proposals overlaid, for comparison
  //   "approvals" the approval queue and the change log
  // Backed by the ?view= query param so it deep-links and survives refresh. A whitelist
  // rather than a ternary, so an unknown value falls back cleanly — which also folds
  // the retired ?view=joints links onto Approved View, where joints now live.
  const VIEWS = ["approved", "proposed", "approvals"];
  const viewParam = searchParams.get("view") ?? "";
  const activeView = VIEWS.includes(viewParam) ? viewParam : "approved";
  const setActiveView = (v: string) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("view", v);
        return next;
      },
      { replace: true },
    );


  const BACK: Record<string, { to: string; label: string }> = {
    capacity: { to: "/risk-concentrations/capacity", label: "← Back to Capacity" },
    // Where "Set up limits now" comes from, after approving a capital source with no
    // limits. The round trip has to land back there or the prompt is a one-way door.
    "manage-capital": {
      to: "/risk-concentrations/manage-capital",
      label: "← Back to Manage Capital",
    },
  };
  const back = BACK[fromParam ?? ""] ?? {
    to: "/risk-concentrations",
    label: "← Back to Monitor",
  };
  const backTo = back.to;
  const backLabel = back.label;

  // ?define=1 / ?jointId= are the deep links Monitor and Capacity emit for a joint row.
  const defineParam = searchParams.get("define") === "1";
  const jointIdParam = searchParams.get("jointId") ?? undefined;

  // The shared filter state, on the same params and with the same rules as every other
  // page — so the selection survives navigation between all four pages and both Manage
  // limits tabs, and a scope toggle no longer discards the book.
  const prcFilters = usePrcFilters(searchParams, setSearchParams);
  const { scope, facility, product, investor } = prcFilters;
  const facilityType: FacilityType = prcFilters.facilityType;

  // Declared after the filters because it depends on them: the limit set, the activation
  // list and the proposal log are all per-investor.
  const store = useLimitConfigStore(investor);
  // The investor's facilities that have risk limits, from the database: what an external limit can be added to.
  const investorFacilities = useInvestorScope(investor).facilities.filter((f) => f.hasLimits);

  const [jointDialogOpen, setJointDialogOpen] = useState(false);
  const [editingJoint, setEditingJoint] = useState<JointDef | null>(null);
  // Off by default: the comparison is the point of the tab, and hiding every unchanged
  // row would turn it back into the queue it replaced.
  const [pendingOnly, setPendingOnly] = useState(false);

  // Joints are scoped to a facility CODE (CAP1 / CAP2 / INTERNAL), while the toggle
  // above the table is a facility TYPE. External covers both books, so the two are not
  // interchangeable — hence the fetch-all-then-filter rather than a per-code query.
  const { data: allJointDefs = [] } = useJointDefs();

  // Joints are scoped by facility AND by investor, though only the first is a column on
  // prc_limit_joints. A joint has no investor of its own — it is a named set of limits —
  // so its investor is its MEMBERS'. A joint is shown only when every member resolves
  // inside the current investor's limit set.
  //
  // That mirrors the rule the backend already applies when it builds a joint row: an
  // unresolved member is refused outright rather than dropped, because dropping one would
  // shrink the set the joint is judged on and overstate its capacity. Here the consequence
  // is milder — a Wafra joint simply does not appear under Navitas — but the rule is the
  // same one, deliberately.
  const resolvableRefs = useMemo(
    () =>
      new Set([
        ...store.internalLimits.map((r) => r.limitId),
        ...store.externalLimits.map((r) => r.limitId),
      ]),
    [store.internalLimits, store.externalLimits],
  );
  const jointRows = useMemo(
    () =>
      allJointDefs
        .filter((j) =>
          facilityType === "internal"
            ? j.facilityCode === "INTERNAL"
            : j.facilityCode !== "INTERNAL" &&
              matchesFacility(j.facilityCode, facility) &&
              matchesProduct(j.facilityCode, product),
        )
        // Only once the limit lists have actually loaded — an empty set during the first
        // render would otherwise blank every joint for a frame.
        .filter(
          (j) =>
            resolvableRefs.size === 0 ||
            j.members.every((m) => resolvableRefs.has(m.memberRef)),
        ),
    [allJointDefs, facilityType, facility, product, resolvableRefs],
  );
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [proposalMsg, setProposalMsg] = useState<string | null>(null);

  // "Apply to snapshots" date-range filter (external MODIFY only). File dates are
  // yyyy-MM-dd strings, so string comparison is chronological.
  const [snapFrom, setSnapFrom] = useState("");
  const [snapTo, setSnapTo] = useState("");

  // Internal rows are all facilityCode INTERNAL, so the book filter only ever narrows the
  // external side — matchesFacility returns true for ALL, which is what Internal always uses.
  const rows = useMemo(() => {
    const list = facilityType === "external" ? store.externalLimits : store.internalLimits;
    // Facility AND product, intersected — the same rule the read endpoints apply, so a
    // selection means the same thing here as it does on Monitor. Internal limits are not
    // scoped to a book at all, so neither narrows them.
    return facilityType === "external"
      ? list.filter(
          (r) =>
            matchesFacility(r.facilityCode, facility) &&
            matchesProduct(r.facilityCode, product),
        )
      : list;
  }, [facilityType, facility, product, store.externalLimits, store.internalLimits]);

  const sortedFileDates = useMemo(() => [...store.fileDates].sort(), [store.fileDates]);
  const visibleFileDates = useMemo(
    () =>
      sortedFileDates.filter((d) => (!snapFrom || d >= snapFrom) && (!snapTo || d <= snapTo)),
    [sortedFileDates, snapFrom, snapTo],
  );
  const allVisibleSelected =
    visibleFileDates.length > 0 && visibleFileDates.every((d) => form.applyFileDates.includes(d));

  // Reset the snapshot filter each time the dialog opens.
  useEffect(() => {
    if (formOpen) {
      setSnapFrom("");
      setSnapTo("");
    }
  }, [formOpen]);

  // Deep-link landing behaviour: open the right form once on arrival.
  // Skipped when the link targets a joint — that opens the joint dialog below instead,
  // and opening the limit form underneath it would stack two dialogs.
  //
  // `?facility=` is the SAME query param PrcFilterBar's Scope toggle writes to
  // (usePrcFilters -> setScope), so without a "consume once" guard this effect re-fired
  // every time Scope was switched after arrival, popping the Add-limit form open again —
  // toggling External/Internal is not a deep link, it's routine use of a control already
  // on the page. `consumedDeepLink` makes this fire at most once per page load, exactly
  // when it's meant to: the initial landing from a link like limitsDeepLink().
  const consumedDeepLink = useRef(false);
  useEffect(() => {
    // Set on the FIRST run regardless of outcome -- a bare `/manage-limits` landing (no
    // `?facility=` yet) must not leave the guard open for the user's first Scope toggle to
    // walk through as if it were the deep link.
    if (consumedDeepLink.current) return;
    consumedDeepLink.current = true;
    if (!facilityParam || jointIdParam || defineParam) return;
    const list = facilityParam === "external" ? store.externalLimits : store.internalLimits;
    if (limitIdParam) {
      const row = list.find((r) => r.limitId === limitIdParam);
      if (row) {
        setForm(formFromRow(row));
        setFormOpen(true);
      }
    } else {
      setForm(emptyForm());
      setFormOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityParam, limitIdParam]);

  // ?define=1 opens a blank joint dialog; ?jointId= opens that joint for editing. The
  // second waits for the definitions to load, hence the length in the deps.
  useEffect(() => {
    if (defineParam && !jointIdParam) {
      setEditingJoint(null);
      setJointDialogOpen(true);
      return;
    }
    if (!jointIdParam) return;
    const j = allJointDefs.find((d) => d.jointId === jointIdParam);
    if (!j) return;
    setEditingJoint(j);
    setJointDialogOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defineParam, jointIdParam, allJointDefs.length]);

  function openAdd() {
    setForm(emptyForm());
    setFormError(null);
    setFormOpen(true);
  }

  function openModify(row: LimitConfigRow) {
    setForm(formFromRow(row));
    setFormError(null);
    setFormOpen(true);
  }

  function openDefineJoint() {
    setEditingJoint(null);
    setJointDialogOpen(true);
  }

  function openEditJoint(j: JointDef) {
    setEditingJoint(j);
    setJointDialogOpen(true);
  }

  // Limit categories copied from the external books that are not switched on yet.
  // Internal-only by construction — external limits have no PENDING state.
  const activatable = facilityType === "internal" ? store.activatableLimits : [];

  // An activation is a MODIFY against a row that is currently PENDING. It travels the
  // ordinary propose -> approve path (the backend's _apply_internal flips the status when
  // it applies), so the only difference here is wording: an approver reading "Propose
  // change" would not know the limit is about to start being enforced for the first time.
  const isActivation =
    !!form.limitId && activatable.some((r) => r.limitId === form.limitId);

  // A new limit needs the dimension/values that define what it measures.
  const isInternalAdd = facilityType === "internal" && !form.limitId;
  const isExternalAdd = facilityType === "external" && !form.limitId;
  const isExternalModify = facilityType === "external" && !!form.limitId;
  const extDim = dimSpec(form.extDimension);

  // Default the snapshot picker to "all" once the form is open for an external MODIFY
  // and the file-date list has loaded — preserving the historical all-snapshots behaviour.
  useEffect(() => {
    if (
      formOpen &&
      isExternalModify &&
      form.applyFileDates.length === 0 &&
      store.fileDates.length
    ) {
      setForm((f) => ({ ...f, applyFileDates: [...store.fileDates] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formOpen, isExternalModify, store.fileDates]);

  async function submitForm() {
    const threshold = Number(form.thresholdPct);
    if (!form.categoryGroup.trim() || !form.limitDesc.trim()) {
      setFormError("Category and limit description are required.");
      return;
    }
    if (!Number.isFinite(threshold) || threshold <= 0) {
      setFormError("Enter a threshold percentage greater than zero.");
      return;
    }
    if (!form.reason.trim()) {
      setFormError("A reason is required for every proposal.");
      return;
    }

    // A new external limit is for the facility chosen in the form (the investor's first facility with limits until one is picked); a
    // change to an existing one is filed under that limit's own facility, which the form carries from its row.
    const externalFacility = form.facilityCode || (form.limitId ? "" : (investorFacilities[0]?.code ?? ""));
    if (facilityType === "external" && !externalFacility) {
      setFormError("This investor has no facility with risk limits to put a limit on.");
      return;
    }

    const input: ProposeInput = {
      limitId: form.limitId,
      facilityType,
      facilityCode: facilityType === "internal" ? "INTERNAL" : externalFacility,
      categoryGroup: form.categoryGroup.trim(),
      limitDesc: form.limitDesc.trim(),
      newThresholdPct: threshold,
      newCapitalCapDollars: form.capitalCap.trim() === "" ? null : Number(form.capitalCap),
      effectiveStartDate: format(form.effectiveStartDate, "yyyy-MM-dd"),
      reason: form.reason.trim(),
    };

    if (isInternalAdd) {
      const matchValues = form.matchValues
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
      if (!matchValues.length) {
        setFormError("Enter at least one match value for the selected dimension.");
        return;
      }
      if (form.matchOperator === "SIC_RANGE" && matchValues.length !== 2) {
        setFormError("A SIC range needs exactly two values: from, to.");
        return;
      }
      input.dimension = form.dimension;
      input.matchOperator = form.matchOperator;
      input.matchValues = matchValues;
    }

    if (isExternalAdd) {
      const matchValues = form.matchValues
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
      const { min, max } = navitasArity(form.extOperator);
      if (matchValues.length < min || matchValues.length > max) {
        setFormError(
          min === 0
            ? "This operator takes no values — leave the values field blank."
            : min === max
              ? `This operator needs exactly ${min} value${min === 1 ? "" : "s"}.`
              : `This operator needs between ${min} and ${max} values.`,
        );
        return;
      }
      input.dimension = form.extDimension;
      input.matchOperator = form.extOperator;
      input.matchValues = matchValues;
      input.measure = form.measure;
    }

    if (isExternalModify) {
      if (store.fileDates.length && form.applyFileDates.length === 0) {
        setFormError("Select at least one snapshot date to apply the change to.");
        return;
      }
      // Omit when every snapshot is selected (server treats that as "all").
      if (form.applyFileDates.length && form.applyFileDates.length < store.fileDates.length) {
        input.applyFileDates = form.applyFileDates;
      }
    }

    // Captured before the propose(), which refetches the limits and empties this row out
    // of `activatable` — recomputing isActivation afterwards would read false.
    const wasActivation = isActivation;

    try {
      setSubmitting(true);
      await store.propose(input);
      setProposalMsg(
        wasActivation
          ? `"${input.limitDesc}" submitted for activation. It starts being enforced once approved.`
          : form.limitId
            ? `Change to "${input.limitDesc}" submitted for approval.`
            : `New limit "${input.limitDesc}" submitted for approval.`,
      );
      setFormOpen(false);
      setFormError(null);
      setActiveView("proposed");
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Failed to submit proposal.");
    } finally {
      setSubmitting(false);
    }
  }

  const logColumns = useMemo<ColumnDef<LimitProposal>[]>(
    () => [
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        // New column: the log now carries joint proposals as well as limit ones, and
        // "Add joint" vs "Add limit" is not inferable from the other cells. Every
        // pre-existing row has changeType undefined, which correctly reads "Limit".
        id: "type",
        header: "Type",
        cell: ({ row }) => (
          <Badge variant="outline" className="text-[10px] uppercase tracking-[0.1em]">
            {CHANGE_TYPE_LABEL[row.original.changeType ?? "MODIFY"] ?? "Limit"}
          </Badge>
        ),
      },
      {
        id: "facility",
        header: "Facility",
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">{row.original.facilityCode}</span>
        ),
      },
      {
        id: "limit",
        header: "Limit",
        cell: ({ row }) => {
          const joint = isJointChange(row.original.changeType);
          const members =
            row.original.memberLimitDescs ?? row.original.jointMemberRefs ?? undefined;
          return (
            <div>
              <div className="flex items-start gap-1.5 font-medium">
                {joint && <Link2 className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />}
                {joint ? (row.original.jointName ?? row.original.limitDesc) : row.original.limitDesc}
              </div>
              <div className="text-[11px] text-muted-foreground">
                {joint && members?.length ? (
                  members.join(" + ")
                ) : (
                  <span className="uppercase tracking-[0.12em]">{row.original.categoryGroup}</span>
                )}
              </div>
            </div>
          );
        },
      },
      {
        id: "threshold",
        header: "Threshold",
        cell: ({ row }) => (
          <span className="tabular-nums">
            {thr(row.original.oldThresholdPct)} →{" "}
            {thr(row.original.newThresholdPct)}
            {/* A joint has no threshold of its own — this column carries its alert
                distance-to-threshold, so label it rather than let it read as a limit. */}
            {isJointChange(row.original.changeType) && (
              <span className="ml-1 text-[11px] text-muted-foreground">alert</span>
            )}
          </span>
        ),
      },
      {
        id: "cap",
        header: "Capital cap",
        cell: ({ row }) =>
          row.original.oldCapitalCapDollars === null &&
          row.original.newCapitalCapDollars === null ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <span className="tabular-nums">
              {capText(row.original.oldCapitalCapDollars)} →{" "}
              {capText(row.original.newCapitalCapDollars)}
            </span>
          ),
      },
      {
        accessorKey: "effectiveStartDate",
        header: "Effective from",
        cell: ({ row }) => <span className="tabular-nums">{row.original.effectiveStartDate}</span>,
      },
      {
        accessorKey: "proposedByRole",
        header: "Proposed by",
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">{row.original.proposedByRole}</span>
        ),
      },
      {
        accessorKey: "reason",
        header: "Reason",
        cell: ({ row }) => (
          <span className="block max-w-[240px] text-xs text-muted-foreground">
            {row.original.reason}
          </span>
        ),
      },
      {
        id: "review",
        header: "Reviewed by",
        cell: ({ row }) =>
          row.original.reviewedByRole ? (
            <div className="max-w-[220px]">
              <div className="text-xs">{row.original.reviewedByRole}</div>
              {/* the date the log is sorted on */}
              {row.original.reviewedAt && !Number.isNaN(Date.parse(row.original.reviewedAt.replace(" ", "T"))) && (
                <div className="text-[11px] tabular-nums text-muted-foreground">
                  {format(new Date(row.original.reviewedAt.replace(" ", "T")), "d MMM yyyy, HH:mm")}
                </div>
              )}
              <div className="text-[11px] text-muted-foreground">{row.original.reviewNotes}</div>
            </div>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
    ],
    [],
  );

  // The log, latest first by DECISION date (when it was approved or rejected), not by when it was proposed. A proposal still waiting has
  // no decision yet, so those sit at the top (newest proposed first) and the decided ones follow, newest decision first; a tie (rows
  // closed in one statement) falls back to the proposal date.
  const logRows = useMemo(() => {
    const at = (iso: string | null | undefined) => {
      const t = iso ? Date.parse(iso.replace(" ", "T")) : NaN;
      return Number.isNaN(t) ? 0 : t;
    };
    return [...store.proposals].sort((a, b) => {
      const aOpen = !a.reviewedAt;
      const bOpen = !b.reviewedAt;
      if (aOpen !== bOpen) return aOpen ? -1 : 1;
      if (!aOpen && at(b.reviewedAt) !== at(a.reviewedAt)) return at(b.reviewedAt) - at(a.reviewedAt);
      return at(b.proposedAt) - at(a.proposedAt);
    });
  }, [store.proposals]);

  const logTable = useReactTable({
    data: logRows,
    columns: logColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  // ── Proposed View: the Approved View rows with proposals laid over them ──────
  //
  // Was an approval queue plus a log, which answered "what is waiting for me" but not
  // "what would the book look like if I said yes". A business reader needs the current
  // and proposed figures side by side on the SAME row, so this is the approved set
  // left-joined to the pending proposals, plus the ADDs that have no approved
  // counterpart yet. The queue and the log moved to their own tab, unchanged.
  const comparisonRows = useMemo(() => {
    const pending = store.pendingProposals;
    const out: ComparisonRow[] = [];

    for (const r of rows) {
      out.push({
        key: `limit:${r.limitId}`,
        kind: "limit",
        categoryGroup: r.categoryGroup,
        limitDesc: r.limitDesc,
        currentThresholdPct: r.thresholdPct,
        currentCapDollars: r.capitalCapDollars,
        effectiveStartDate: r.effectiveStartDate,
        proposal: pending.find((p) => !isJointChange(p.changeType) && p.limitId === r.limitId) ?? null,
        isNew: false,
      });
    }

    for (const j of jointRows) {
      out.push({
        key: `joint:${j.jointId}`,
        kind: "joint",
        categoryGroup: "Joint",
        limitDesc: j.jointName,
        // A joint has no threshold of its own — on the utilisation basis it is always
        // 100. What is configurable, and what the proposal's threshold column actually
        // carries, is its alert distance.
        currentThresholdPct: j.alertDistToThresholdPct,
        currentCapDollars: null,
        effectiveStartDate: j.effectiveStartDate ?? null,
        proposal: pending.find((p) => isJointChange(p.changeType) && p.jointId === j.jointId) ?? null,
        isNew: false,
        memberCount: j.members.length,
      });
    }

    // Pending ACTIVATIONS. These are MODIFY proposals whose limitId points at a row that
    // is PENDING in prc_limit_config, so it is not in `rows` above and the ADD loop below
    // skips it (changeType is MODIFY, not ADD) — without this it would be invisible here,
    // which is where submitting one sends you.
    //
    // currentThresholdPct is null, not the copied external figure: nothing is being
    // enforced today, and printing the external book's number under "Current threshold"
    // would claim otherwise. isNew carries the same meaning it does for an ADD — new to
    // the internal book.
    for (const r of activatable) {
      const p = pending.find((x) => !isJointChange(x.changeType) && x.limitId === r.limitId);
      if (!p) continue;
      out.push({
        key: `activate:${r.limitId}`,
        kind: "limit",
        categoryGroup: r.categoryGroup,
        limitDesc: r.limitDesc,
        currentThresholdPct: null,
        currentCapDollars: null,
        effectiveStartDate: r.effectiveStartDate,
        proposal: p,
        isNew: true,
      });
    }

    // ADD / ADD_JOINT proposals describe something that does not exist yet, so they
    // have no row above to attach to and would otherwise be invisible here — exactly
    // the changes a reviewer most needs to see.
    for (const p of pending) {
      const isAdd = p.changeType === "ADD" || p.changeType === "ADD_JOINT";
      if (!isAdd) continue;
      if (p.facilityType !== facilityType) continue;
      if (!matchesFacility(p.facilityCode, facility)) continue;
      out.push({
        key: `new:${p.proposalId}`,
        kind: isJointChange(p.changeType) ? "joint" : "limit",
        categoryGroup: p.categoryGroup,
        limitDesc: p.limitDesc,
        currentThresholdPct: null,
        currentCapDollars: null,
        effectiveStartDate: p.effectiveStartDate ?? null,
        proposal: p,
        isNew: true,
        memberCount: p.jointMemberRefs?.length,
      });
    }

    return out;
  }, [rows, jointRows, activatable, store.pendingProposals, facilityType, facility]);

  const visibleComparisonRows = pendingOnly
    ? comparisonRows.filter((r) => r.proposal)
    : comparisonRows;

  return (
    <ManageLimitsShell backTo={backTo} backLabel={backLabel}>
      <PageHeader
        title="Manage Limits"
      />

      {/* Above the tabs, not inside one: a limit is proposed from Approved View and a
          joint can be proposed from a deep link on any tab, so a banner scoped to a
          single tab was invisible half the time. */}
      {proposalMsg && (
        <div className="mt-4 rounded-lg border border-amber/50 bg-amber-dim/40 px-4 py-3 text-sm">
          {proposalMsg} Approve it under Approvals &amp; log to apply it to the live data.
        </div>
      )}

      {/* One filter bar for the page, above the tabs, rather than a copy inside each of
          them. The two copies were byte-identical ToggleGroups driving the same state, so
          they could not disagree — but they were also a different widget from the one Monitor
          and Snapshot use, which is the inconsistency this replaces.
          Hidden on Approvals & log: that tab lists every pending proposal across both scopes
          and books, so a filter bar over it would claim to narrow something it does not. */}
      {activeView !== "approvals" && (
        <div className="mt-5 space-y-2">
          <PrcFilterBar
            scope={scope}
            onScopeChange={prcFilters.setScope}
            facility={facility}
            onFacilityChange={prcFilters.setFacility}
            product={product}
            onProductChange={prcFilters.setProduct}
            investor={investor}
            onInvestorChange={prcFilters.setInvestor}
          />
        </div>
      )}

      <Tabs value={activeView} onValueChange={setActiveView} className="mt-4">
        <TabsList>
          <TabsTrigger value="approved">Approved View</TabsTrigger>
          <TabsTrigger value="proposed">
            Proposed View
            {store.pendingProposals.length ? ` (${store.pendingProposals.length})` : ""}
          </TabsTrigger>
          <TabsTrigger value="approvals">
            Approvals &amp; log
            {store.pendingProposals.length ? ` (${store.pendingProposals.length})` : ""}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="approved">
          {/* No facility capacity panel here. Commitment / drawn / remaining capacity is a
              statement about the BOOK, and this page configures LIMITS — the two never share
              a row of data. It was put here because joints moved onto this page and the
              sum-of-headrooms vs remaining-capacity contrast is the argument a joint makes,
              but that argument is Capacity's subject and it still leads with the same three
              figures. Removed rather than moved. */}
          <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
            <Button size="sm" onClick={openAdd}>
              + Add Limit
            </Button>
            {/* Beside "Add limit", not merged into it: a joint collects a member set,
                a limit collects a rule, and they apply through different code paths.
                See DefineJointDialog's header comment. */}
            <Button size="sm" variant="outline" onClick={openDefineJoint}>
              + Define Joint
            </Button>
          </div>

          <div className="mt-5 overflow-hidden rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  {["Category", "Limit", "Threshold", "Capital cap", "Effective from", "Action"].map(
                    (h) => (
                      <TableHead key={h} className="text-[11px] uppercase tracking-[0.12em]">
                        {h}
                      </TableHead>
                    ),
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.limitId}>
                    <TableCell className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                      {r.categoryGroup}
                    </TableCell>
                    <TableCell className="font-medium">{r.limitDesc}</TableCell>
                    <TableCell className="tabular-nums">{thr(r.thresholdPct)}</TableCell>
                    <TableCell className="tabular-nums">{capText(r.capitalCapDollars)}</TableCell>
                    <TableCell className="tabular-nums">{r.effectiveStartDate}</TableCell>
                    <TableCell>
                      {r.pendingProposalId ? (
                        <Badge
                          variant="outline"
                          className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground"
                        >
                          Pending
                        </Badge>
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => openModify(r)}>
                          Propose change
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}

                {/* Joints in the SAME table as the limits, marked rather than
                    separated — they are approved configuration exactly like a limit,
                    and they already share a row shape everywhere else in the product. */}
                {jointRows.map((j) => (
                  <TableRow key={j.jointId}>
                    <TableCell className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                      Joint
                    </TableCell>
                    {/* Name and figures only. The member list and the alert-distance
                        caption used to sit under them; they made a joint row three lines
                        tall in a table of one-line rows, and both are on the joint's own
                        edit dialog where they can be acted on. */}
                    <TableCell className="font-medium">
                      <span className="flex items-start gap-1.5">
                        <Link2 className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
                        <span>{j.jointName}</span>
                      </span>
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {j.alertDistToThresholdPct === null
                        ? "—"
                        : thr(j.alertDistToThresholdPct)}
                    </TableCell>
                    <TableCell className="tabular-nums">—</TableCell>
                    <TableCell className="tabular-nums">{j.effectiveStartDate ?? "—"}</TableCell>
                    <TableCell>
                      {j.pendingProposalId ? (
                        <Badge
                          variant="outline"
                          className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground"
                        >
                          Pending
                        </Badge>
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => openEditJoint(j)}>
                          Propose change
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}

                {!rows.length && !jointRows.length && (
                  <TableRow>
                    <TableCell colSpan={6} className="py-6 text-center text-xs text-muted-foreground">
                      No {facilityType} limits or joints defined yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* ── Available to activate ────────────────────────────────────────────
              A SEPARATE table, below the live one, on purpose. These rows are in
              prc_limit_config but PENDING, which means inert: v_prc_concentrations
              reads status = 'ACTIVE', so they produce no actuals, appear on no page
              and are scored by no alert. Mixing them into the table above would
              read as "these are being enforced", which is exactly wrong.

              The threshold shown is a STARTING POINT copied from the external book
              (CAP 1's, else CAP 2's — see sql/72_), not an internal decision. An
              external category can carry a different threshold per book while an
              internal limit has only one, so it is expected to be retuned before
              anyone switches it on. */}
          {activatable.length > 0 && (
            <div className="mt-8">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="text-sm font-semibold">
                  Available to activate
                  <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                    ({activatable.length})
                  </span>
                </h2>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Limit categories the external books are governed by, available to track as
                internal limits. None of these is being enforced: they produce no actuals and
                trigger no alerts until activated. The threshold shown is the external book&apos;s
                value as a starting point — set your own when you activate.
              </p>

              <div className="mt-4 overflow-hidden rounded-lg border border-dashed border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {["Category", "Limit", "Measures", "External threshold", "Action"].map((h) => (
                        <TableHead key={h} className="text-[11px] uppercase tracking-[0.12em]">
                          {h}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activatable.map((r) => (
                      <TableRow key={r.limitId} className="opacity-90">
                        <TableCell className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                          {r.categoryGroup}
                        </TableCell>
                        <TableCell className="font-medium">{r.limitDesc}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {ruleText(r)}
                        </TableCell>
                        <TableCell className="tabular-nums text-muted-foreground">
                          {thr(r.thresholdPct)}
                        </TableCell>
                        <TableCell>
                          {r.pendingProposalId ? (
                            <Badge
                              variant="outline"
                              className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground"
                            >
                              Awaiting approval
                            </Badge>
                          ) : (
                            <Button variant="outline" size="sm" onClick={() => openModify(r)}>
                              Activate
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="proposed">
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                <Checkbox
                  checked={pendingOnly}
                  onCheckedChange={(c) => setPendingOnly(!!c)}
                />
                Pending only
              </label>
            </div>
          </div>

          <div className="mt-4 overflow-x-auto rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  {[
                    "Category",
                    "Limit",
                    "Current threshold",
                    "Proposed",
                    "Δ",
                    "Current cap",
                    "Proposed cap",
                    "Proposed by",
                    "Status",
                  ].map((h) => (
                    <TableHead key={h} className="text-[11px] uppercase tracking-[0.12em]">
                      {h}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleComparisonRows.map((r) => {
                  const p = r.proposal;
                  const isJointRow = r.kind === "joint";
                  const delta =
                    p && r.currentThresholdPct !== null
                      ? p.newThresholdPct - r.currentThresholdPct
                      : null;
                  return (
                    <TableRow key={r.key} className={cn(p && "bg-amber-dim/20")}>
                      <TableCell className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                        {r.categoryGroup}
                      </TableCell>
                      <TableCell className="font-medium">
                        <span className="flex items-start gap-1.5">
                          {isJointRow && (
                            <Link2 className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
                          )}
                          <span>
                            {r.limitDesc}
                            {isJointRow && r.memberCount ? (
                              <span className="ml-1.5 text-[11px] font-normal text-muted-foreground">
                                · {r.memberCount} limits
                              </span>
                            ) : null}
                            {r.isNew && (
                              <Badge
                                variant="outline"
                                className="ml-1.5 text-[10px] uppercase tracking-[0.1em]"
                              >
                                New
                              </Badge>
                            )}
                          </span>
                        </span>
                      </TableCell>
                      <TableCell className="tabular-nums">
                        {r.currentThresholdPct === null ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          thr(r.currentThresholdPct)
                        )}
                        {/* A joint's number is its ALERT distance, not a threshold. The
                            column header cannot say both, so the row does. */}
                        {isJointRow && r.currentThresholdPct !== null && (
                          <span className="block text-[11px] text-muted-foreground">
                            alert distance
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="tabular-nums">
                        {p ? (
                          <span className="font-medium">{thr(p.newThresholdPct)}</span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="tabular-nums">
                        {delta === null ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <span
                            className={cn(
                              delta > 0 && "text-amber",
                              delta < 0 && "text-[color:var(--approve)]",
                            )}
                          >
                            {delta > 0 ? "+" : ""}
                            {delta.toFixed(2)}pp
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="tabular-nums">
                        {capText(r.currentCapDollars)}
                      </TableCell>
                      <TableCell className="tabular-nums">
                        {p ? capText(p.newCapitalCapDollars ?? null) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {p ? p.proposedByRole : "—"}
                      </TableCell>
                      {/* No Reason column here. The rationale is a paragraph, not a cell — it
                          truncated to uselessness at 240px and pushed Status off the visible
                          width. It is still read in full on Approvals & log, which is where a
                          proposal is actually judged. */}
                      <TableCell>
                        {p ? (
                          <Badge
                            variant="outline"
                            className="text-[10px] font-semibold uppercase tracking-[0.1em] text-amber"
                          >
                            Pending
                          </Badge>
                        ) : (
                          <span className="text-[11px] text-muted-foreground">Approved</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}

                {!visibleComparisonRows.length && (
                  <TableRow>
                    <TableCell colSpan={9} className="py-6 text-center text-xs text-muted-foreground">
                      {pendingOnly
                        ? "No proposals are waiting on this side."
                        : `No ${facilityType} limits or joints defined yet.`}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="approvals">
          {/* activationIds comes from store.activatableLimits, NOT `activatable` — the
              queue lists proposals across both scopes (see the note above the tabs), so
              scoping it to the current facility toggle would drop the badge whenever the
              page happened to be showing External. */}
          <ApprovalQueue
            proposals={store.pendingProposals}
            onReview={store.review}
            activationIds={new Set(store.activatableLimits.map((r) => r.limitId))}
            canDecide={hasTier("contributor")}
          />

          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-sm font-semibold">Manage Limits log</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto rounded-lg border border-border">
                <Table>
                  <TableHeader>
                    {logTable.getHeaderGroups().map((hg) => (
                      <TableRow key={hg.id}>
                        {hg.headers.map((h) => (
                          <TableHead key={h.id} className="text-[11px] uppercase tracking-[0.12em]">
                            {h.isPlaceholder
                              ? null
                              : flexRender(h.column.columnDef.header, h.getContext())}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {logTable.getRowModel().rows.map((r) => (
                      <TableRow key={r.id}>
                        {r.getVisibleCells().map((c) => (
                          <TableCell key={c.id} className="align-top">
                            {flexRender(c.column.columnDef.cell, c.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Mounted outside the tabs: "+ Define joint" lives on Approved View, but a
          ?jointId= deep link can land on any tab and must still open it. */}
      <DefineJointDialog
        open={jointDialogOpen}
        onOpenChange={setJointDialogOpen}
        facilityType={facilityType}
        externalLimits={store.externalLimits}
        internalLimits={store.internalLimits}
        propose={store.propose}
        editJoint={editingJoint}
        onSubmitted={(message) => {
          setProposalMsg(message);
          setEditingJoint(null);
        }}
      />

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <PrcDialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {isActivation ? "Activate limit" : form.limitId ? "Propose change" : "Add new limit"}
            </DialogTitle>
            <DialogDescription>
              {isActivation ? (
                <>
                  This limit is not being enforced yet. Approving this proposal switches it on
                  and starts scoring it against the threshold you set below.
                </>
              ) : (
                <>
                  {facilityType === "internal" ? "Internal" : "External"} limit
                  {/* Named, not implied. A limit is created under whichever investor's
                      appetite is on screen (the store stamps it onto the proposal), and
                      without this the form gives no clue which — so the same dialog under
                      two investors would look identical and produce different rows. */}
                  {facilityType === "internal" && (
                    <> for <strong>{INVESTOR_LABEL[investor]}</strong></>
                  )}{" "}
                  — this creates a proposal; it applies to the live data only once approved.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {/* The rule is fixed on an activation: it was copied from the external book and
              this flow decides the threshold, not what the limit measures. Showing it
              read-only beats leaving the approver to guess from the description. */}
          {isActivation && (
            <div className="rounded-md border border-border/60 bg-muted/20 px-3 py-2">
              <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                Measures
              </p>
              <p className="mt-0.5 text-sm">
                {ruleText(activatable.find((r) => r.limitId === form.limitId)!)}
              </p>
            </div>
          )}

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="ml-category">Category</Label>
                <Input
                  id="ml-category"
                  value={form.categoryGroup}
                  onChange={(e) => setForm((f) => ({ ...f, categoryGroup: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ml-threshold">Threshold %</Label>
                <Input
                  id="ml-threshold"
                  type="number"
                  value={form.thresholdPct}
                  onChange={(e) => setForm((f) => ({ ...f, thresholdPct: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ml-desc">Limit description</Label>
              <Input
                id="ml-desc"
                value={form.limitDesc}
                onChange={(e) => setForm((f) => ({ ...f, limitDesc: e.target.value }))}
              />
            </div>

            {isExternalModify && store.fileDates.length > 0 && (
              <div className="space-y-2 rounded-md border border-border/60 bg-muted/20 p-3">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                    Apply to snapshots
                  </p>
                  <span className="text-[11px] text-muted-foreground tabular-nums">
                    {form.applyFileDates.length} of {store.fileDates.length} selected
                  </span>
                </div>

                {/* Date-range filter */}
                <div className="flex flex-wrap items-end gap-2">
                  <div className="space-y-1">
                    <Label htmlFor="snap-from" className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
                      From
                    </Label>
                    <Input
                      id="snap-from"
                      type="date"
                      className="h-8 w-[140px] text-xs [&::-webkit-calendar-picker-indicator]:invert"
                      min={sortedFileDates[0]}
                      max={sortedFileDates[sortedFileDates.length - 1]}
                      value={snapFrom}
                      onChange={(e) => setSnapFrom(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="snap-to" className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
                      To
                    </Label>
                    <Input
                      id="snap-to"
                      type="date"
                      className="h-8 w-[140px] text-xs [&::-webkit-calendar-picker-indicator]:invert"
                      min={sortedFileDates[0]}
                      max={sortedFileDates[sortedFileDates.length - 1]}
                      value={snapTo}
                      onChange={(e) => setSnapTo(e.target.value)}
                    />
                  </div>
                  {(snapFrom || snapTo) && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 text-xs"
                      onClick={() => {
                        setSnapFrom("");
                        setSnapTo("");
                      }}
                    >
                      Clear
                    </Button>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Checkbox
                      checked={allVisibleSelected}
                      onCheckedChange={(c) =>
                        setForm((f) => {
                          const set = new Set(f.applyFileDates);
                          if (c) visibleFileDates.forEach((d) => set.add(d));
                          else visibleFileDates.forEach((d) => set.delete(d));
                          return { ...f, applyFileDates: [...set] };
                        })
                      }
                    />
                    {snapFrom || snapTo ? "Select all shown" : "Select all"}
                  </label>
                  <span className="text-[11px] text-muted-foreground tabular-nums">
                    {visibleFileDates.length} shown
                  </span>
                </div>

                {visibleFileDates.length === 0 ? (
                  <p className="py-4 text-center text-xs text-muted-foreground">
                    No snapshots in this range.
                  </p>
                ) : (
                  <div className="max-h-48 grid grid-cols-2 gap-2 overflow-y-auto pr-1">
                    {visibleFileDates.map((d) => {
                      const checked = form.applyFileDates.includes(d);
                      return (
                        <label key={d} className="flex items-center gap-2 text-sm">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(c) =>
                              setForm((f) => ({
                                ...f,
                                applyFileDates: c
                                  ? [...f.applyFileDates, d]
                                  : f.applyFileDates.filter((x) => x !== d),
                              }))
                            }
                          />
                          <span suppressHydrationWarning>
                            {format(new Date(`${d}T00:00:00`), "d MMM yyyy")}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}

                <p className="text-[11px] text-muted-foreground">
                  The new threshold reprices only the selected snapshots. The current threshold
                  shown in Manage Limits updates only when the latest snapshot is included.
                </p>
              </div>
            )}

            {isInternalAdd && (
              <div className="space-y-3 rounded-md border border-border/60 bg-muted/20 p-3">
                <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                  What this limit measures
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Dimension</Label>
                    <Select
                      value={form.dimension}
                      onValueChange={(v) =>
                        setForm((f) => ({
                          ...f,
                          dimension: v as PrcDimension,
                          matchOperator: defaultOperator(v as PrcDimension),
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DIMENSIONS.map((d) => (
                          <SelectItem key={d.value} value={d.value}>
                            {d.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {form.dimension === "sic_code" && (
                    <div className="space-y-1.5">
                      <Label>Match</Label>
                      <Select
                        value={form.matchOperator}
                        onValueChange={(v) =>
                          setForm((f) => ({ ...f, matchOperator: v as PrcMatchOperator }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="IN">Specific SIC codes</SelectItem>
                          <SelectItem value="SIC_RANGE">SIC range (from, to)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ml-values">
                    {form.dimension === "obligor" ? "Top N" : "Match values"}
                  </Label>
                  <Input
                    id="ml-values"
                    value={form.matchValues}
                    placeholder={valuesHint(form.dimension, form.matchOperator)}
                    onChange={(e) => setForm((f) => ({ ...f, matchValues: e.target.value }))}
                  />
                  <p className="text-[11px] text-muted-foreground">
                    {valuesHint(form.dimension, form.matchOperator)}
                  </p>
                </div>
              </div>
            )}

            {isExternalAdd && (
              <div className="space-y-3 rounded-md border border-border/60 bg-muted/20 p-3">
                <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                  What this limit measures
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Facility</Label>
                    {/* The investor's facilities that have risk limits, from the database (Wafra: CAP 1 and CAP 2). */}
                    <Select
                      value={form.facilityCode || investorFacilities[0]?.code || ""}
                      onValueChange={(v) => setForm((f) => ({ ...f, facilityCode: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="No facility with risk limits" />
                      </SelectTrigger>
                      <SelectContent>
                        {investorFacilities.map((f) => (
                          <SelectItem key={f.code} value={f.code}>
                            {f.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Dimension</Label>
                    <Select
                      value={form.extDimension}
                      onValueChange={(v) =>
                        setForm((f) => ({
                          ...f,
                          extDimension: v as NavitasDimension,
                          extOperator: defaultNavitasOperator(v as NavitasDimension),
                          matchValues: "",
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {NAVITAS_DIMENSIONS.map((d) => (
                          <SelectItem key={d.value} value={d.value}>
                            {d.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Operator</Label>
                    <Select
                      value={form.extOperator}
                      onValueChange={(v) =>
                        setForm((f) => ({ ...f, extOperator: v as NavitasOperator }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {extDim.operators.map((op) => (
                          <SelectItem key={op} value={op}>
                            {operatorLabel(op)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {/* No Measure select: every new external limit is measured on net investment,
                    which `form.measure` is initialised to and still submits.

                    `pv_unguaranteed_residual` remains a supported measure everywhere else and
                    must not be removed — the limit "Present value of unguaranteed residual" is
                    LIVE on both books (navitas_limit_config
                    `both-present-value-of-unguaranteed-residual`, with actuals in
                    risk_concentrations_trend at every snapshot). It keeps rendering, keeps
                    computing, and keeps its LINEAR headroom regime. Only the choice at creation
                    time is gone. */}
                {form.extOperator !== "MEASURE_SUM" && (
                  <div className="space-y-1.5">
                    <Label htmlFor="ml-ext-values">Values</Label>
                    <Input
                      id="ml-ext-values"
                      value={form.matchValues}
                      placeholder={navitasValuesHint(form.extDimension, form.extOperator)}
                      onChange={(e) => setForm((f) => ({ ...f, matchValues: e.target.value }))}
                    />
                    <p className="text-[11px] text-muted-foreground">
                      {navitasValuesHint(form.extDimension, form.extOperator)}
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="ml-cap">Capital cap $ (optional)</Label>
                <Input
                  id="ml-cap"
                  type="number"
                  placeholder="Leave blank for none"
                  value={form.capitalCap}
                  onChange={(e) => setForm((f) => ({ ...f, capitalCap: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Effective from</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="w-full justify-start gap-2">
                      <CalendarIcon className="h-3.5 w-3.5" />
                      <span suppressHydrationWarning>
                        {format(form.effectiveStartDate, "d MMM yyyy")}
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={form.effectiveStartDate}
                      onSelect={(d) => d && setForm((f) => ({ ...f, effectiveStartDate: d }))}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ml-reason">Reason</Label>
              <Textarea
                id="ml-reason"
                rows={3}
                placeholder="Why is this change being proposed?"
                value={form.reason}
                onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
              />
            </div>

            {formError && <p className="text-xs text-destructive">{formError}</p>}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={submitForm} disabled={submitting}>
              {submitting
                ? "Submitting…"
                : isActivation
                  ? "Submit for activation"
                  : "Submit proposal"}
            </Button>
          </DialogFooter>
        </PrcDialogContent>
      </Dialog>
    </ManageLimitsShell>
  );
}

function ApprovalQueue({
  proposals,
  onReview,
  activationIds,
  canDecide,
}: {
  proposals: LimitProposal[];
  onReview: (
    id: string,
    decision: "APPROVED" | "REJECTED",
    notes: string,
  ) => void | Promise<unknown>;
  /** Contributor and above. A reviewer still sees the queue -- they raised what is in
   *  it -- but cannot approve or reject. */
  canDecide: boolean;
  /** limitIds that are still PENDING in prc_limit_config, so a MODIFY against one is an
   *  activation rather than a repricing. Passed in because a proposal row alone cannot
   *  tell the two apart — both are changeType MODIFY. */
  activationIds?: Set<string>;
}) {
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  async function act(p: LimitProposal, decision: "APPROVED" | "REJECTED") {
    const note = (notes[p.proposalId] ?? "").trim();
    if (!note) {
      setErrors((e) => ({ ...e, [p.proposalId]: "Review notes are required." }));
      return;
    }
    try {
      setBusy(p.proposalId);
      await onReview(p.proposalId, decision, note);
      setNotes((n) => ({ ...n, [p.proposalId]: "" }));
      setErrors((e) => ({ ...e, [p.proposalId]: "" }));
    } catch (err) {
      setErrors((e) => ({
        ...e,
        [p.proposalId]: err instanceof Error ? err.message : "Review failed.",
      }));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="text-sm font-semibold">
          Approval queue{proposals.length ? ` (${proposals.length})` : ""}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {proposals.length === 0 ? (
          <p className="text-sm text-muted-foreground">No proposals awaiting review.</p>
        ) : (
          <div className="space-y-4">
            {proposals.map((p) => (
              <div key={p.proposalId} className="rounded-lg border border-border p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <ProposalSummary
                    p={p}
                    isActivation={!!p.limitId && !!activationIds?.has(p.limitId)}
                  />
                  <StatusBadge status={p.status} />
                </div>

                {/* Deciding is the contributor's half of maker-checker; a reviewer
                    raised these and can read the queue but not act on it. The notes box
                    goes with the buttons -- typing into a box with nothing to submit it
                    would be worse than not showing it. Backed by require_tier on
                    POST /proposals/{id}/review, which both buttons call. */}
                {canDecide && (
                  <div className="mt-3 space-y-2">
                    <Textarea
                      rows={2}
                      placeholder="Review notes (required)"
                      value={notes[p.proposalId] ?? ""}
                      onChange={(e) => setNotes((n) => ({ ...n, [p.proposalId]: e.target.value }))}
                    />
                    {errors[p.proposalId] && (
                      <p className="text-xs text-destructive">{errors[p.proposalId]}</p>
                    )}
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        disabled={busy === p.proposalId}
                        onClick={() => act(p, "APPROVED")}
                      >
                        {busy === p.proposalId ? "Applying…" : "Approve"}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={busy === p.proposalId}
                        onClick={() => act(p, "REJECTED")}
                      >
                        Reject
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
